import { Select, Space, Spin, Tag, Tooltip, message } from 'antd';
import { debounce } from 'lodash';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import useLazyQueryWithCallbacks from '../common/CommonUseLazyQuery';
import { DROPDOWN_LIMIT } from '../common/constants';
import useRouter from '../common/useRouter';
import { GET_PROJECT_REGISTER_EQC_LIST } from '../modules/projects/graphql/Queries';

const MAX_TAG_DISPLAY_COUNT = 2;

const getOptionDetails = (val, options) => {
  const option = options?.find((opt) => opt?.value === val);
  return {
    label: option?.label || val,
    url: option?.url || '',
  };
};

const handleEqcClick = (e, url, hasAccess) => {
  e?.stopPropagation();
  if (!hasAccess) {
    message?.warning('Your tenant does not have inspection access');
    return;
  }
  if (url) {
    // eslint-disable-next-line no-undef
    window?.open(url, '_blank');
  }
};

const EditableHyperlinkTag = ({
  label,
  value,
  closable,
  onClose,
  options,
  hasAccess,
}) => {
  const { url } = getOptionDetails(value, options);

  return (
    <Tag
      color="#f2f2f2"
      closable={closable && hasAccess}
      onClose={onClose}
      onClick={(e) => handleEqcClick(e, url, hasAccess)}
      className="hyperlink-tag pointer"
    >
      <span className="eqc-tag-text">{label}</span>
    </Tag>
  );
};

const renderTooltipContent = (
  omittedValues,
  options,
  showLinks = true,
  hasAccess = true,
) => {
  if (!omittedValues || !Array?.isArray(omittedValues)) return null;
  return (
    <div className="eqc-tooltip-content">
      {omittedValues?.map((val) => {
        const valueId = val?.value || val;
        const { label, url } = getOptionDetails(valueId, options);
        return (
          <div key={valueId} className="eqc-tooltip-item-wrapper">
            {showLinks ? (
              <a
                href={hasAccess ? url : ''}
                className="eqc-tooltip-link-item"
                onClick={(e) => {
                  e?.preventDefault();
                  handleEqcClick(e, url, hasAccess);
                }}
              >
                {label}
              </a>
            ) : (
              <span className="eqc-tooltip-link-item edit-mode">{label}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const EqcDropdown = forwardRef(
  (
    {
      value = [],
      onChange,
      onBlur,
      disabled = false,
      hasAccess = true,
      defaultOptions = [],
    },
    ref,
  ) => {
    const [isSelectVisible, setIsSelectVisible] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [options, setOptions] = useState(defaultOptions);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const {
      params: { projectId },
    } = useRouter();

    const selectedValues = useMemo(() => {
      if (Array?.isArray(value)) return value;
      if (typeof value === 'string' && value?.trim() !== '') {
        return value?.split(',');
      }
      return [];
    }, [value]);

    const selectedValuesRef = useRef(selectedValues);
    useEffect(() => {
      selectedValuesRef.current = selectedValues;
    }, [selectedValues]);

    const selectRef = useRef(null);

    useEffect(() => {
      if (defaultOptions?.length > 0) {
        setOptions((prev) => {
          const existing = new Map(prev?.map((opt) => [opt?.value, opt]));
          defaultOptions?.forEach((opt) => {
            if (!existing?.has(opt?.value)) {
              existing?.set(opt?.value, opt);
            }
          });
          return Array?.from(existing?.values());
        });
      }
    }, [defaultOptions]);

    const [fetchEqcList, { loading }] = useLazyQueryWithCallbacks(
      GET_PROJECT_REGISTER_EQC_LIST,
      {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onError: () => {},
      },
    );

    const fetchEqcData = useCallback(
      async (searchText, skipValue, isAppend) => {
        if (!projectId) return;
        try {
          const { data } = await fetchEqcList({
            variables: {
              filter: {
                projectId,
                search: searchText,
                limit: DROPDOWN_LIMIT,
                skip: skipValue,
              },
            },
          });
          const rawData = data?.registerEqcList?.data || [];
          const formattedOptions = rawData?.map((item) => ({
            label: item?.eqcName,
            value: item?.id,
            // eslint-disable-next-line no-undef
            url: `${window?.location?.origin}/projects/${projectId}/eqc/${item?.id}`,
          }));
          if (isAppend) {
            setOptions((prev) => {
              const mergedOptionsMap = new Map(
                prev?.map((opt) => [opt?.value, opt]),
              );
              formattedOptions?.forEach((opt) => {
                if (!mergedOptionsMap?.has(opt?.value)) {
                  mergedOptionsMap?.set(opt?.value, opt);
                }
              });
              return Array?.from(mergedOptionsMap?.values());
            });
          } else {
            setOptions((prevOptions) => {
              const currentSelected = selectedValuesRef?.current;
              const preservedOptions = prevOptions?.filter((opt) =>
                currentSelected?.includes(opt?.value),
              );
              const mergedOptionsMap = new Map();
              preservedOptions?.forEach((opt) =>
                mergedOptionsMap?.set(opt?.value, opt),
              );
              formattedOptions?.forEach((opt) =>
                mergedOptionsMap?.set(opt?.value, opt),
              );
              return Array?.from(mergedOptionsMap?.values());
            });
          }
          if (formattedOptions?.length < DROPDOWN_LIMIT) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        } catch {
          // Silent Error handling
        }
      },
      [projectId, fetchEqcList],
    );

    const debouncedSearch = useMemo(
      () =>
        debounce((val) => {
          setSearchTerm(val);
          setHasMore(true);
          fetchEqcData(val, 0, false);
        }, 500),
      [fetchEqcData],
    );

    const handleSearch = (val) => {
      debouncedSearch(val);
    };

    const handlePopupScroll = (e) => {
      const { target } = e;
      if (
        Math?.abs(
          target?.scrollHeight - (target?.scrollTop + target?.offsetHeight),
        ) < 10
      ) {
        if (loading || !hasMore) return;
        const nextSkip = options?.length;
        fetchEqcData(searchTerm, nextSkip, true);
      }
    };
    useEffect(() => {
      fetchEqcData('', 0, false);
    }, []);

    useImperativeHandle(ref, () => ({
      openEditMode: () => {
        if (!disabled && hasAccess) {
          setIsSelectVisible(true);
          setIsDropdownOpen(true);
          setSearchTerm('');
          fetchEqcData('', 0, false);
        }
      },
    }));

    const isEmpty = selectedValues?.length === 0;

    const handleEditClickAndOpen = useCallback(() => {
      if (!disabled && hasAccess) {
        setIsSelectVisible(true);
        setIsDropdownOpen(true);
        setSearchTerm('');
        fetchEqcData('', 0, false);
      }
    }, [disabled, hasAccess]);

    const handleSelectBlur = useCallback(() => {
      if (onBlur) {
        onBlur();
      }
      setIsSelectVisible(false);
      setIsDropdownOpen(false);
    }, [onBlur]);

    const handleChange = useCallback(
      (newValues, selectedOptions) => {
        setSearchTerm('');
        fetchEqcData('', 0, false);
        if (onChange) {
          onChange(newValues, selectedOptions);
        }
      },
      [onChange],
    );

    useEffect(() => {
      if (isSelectVisible && selectRef?.current) {
        selectRef?.current?.focus();
      }
    }, [isSelectVisible]);

    const ReadOnlyPlaceholder = () => {
      const content = (
        <Select
          mode="multiple"
          placeholder="Enter EQC"
          value={[]}
          disabled={disabled}
          className={`${!disabled ? 'eqc-readonly-select' : ''} `}
          onClick={handleEditClickAndOpen}
          suffixIcon={null}
        />
      );

      if (!hasAccess) {
        return (
          <Tooltip title="Your tenant does not have inspection access">
            <div className="width-percent-100">{content}</div>
          </Tooltip>
        );
      }

      return content;
    };

    const ReadOnlyTextView = () => {
      const visibleValues = selectedValues?.slice(0, MAX_TAG_DISPLAY_COUNT);
      const hiddenValues = selectedValues?.slice(MAX_TAG_DISPLAY_COUNT);
      const hasHidden = hiddenValues?.length > 0;
      return (
        <div className="eqc-readonly-text-container">
          {visibleValues?.map((val, index) => {
            const { label, url } = getOptionDetails(val, options);
            const showComma = index < visibleValues?.length - 1 || hasHidden;
            return (
              <React.Fragment key={val}>
                <Tooltip title={label}>
                  <span
                    onClick={(e) => handleEqcClick(e, url, hasAccess)}
                    className="eqc-text-link"
                  >
                    {label}
                  </span>
                </Tooltip>
                {showComma && <span className="eqc-separator">, </span>}
              </React.Fragment>
            );
          })}

          {hasHidden && (
            <Tooltip
              title={renderTooltipContent(
                hiddenValues,
                options,
                true,
                hasAccess,
              )}
              placement="top"
              classNames={{
                root: 'eqc-hyperlink-tooltip',
                container: 'eqc-hyperlink-container',
              }}
            >
              <span className="eqc-overflow-text">
                <span className="eqc-overflow-nowrap">
                  +{hiddenValues?.length} Eqc
                </span>
              </span>
            </Tooltip>
          )}
        </div>
      );
    };

    return (
      <div className="eqc-dropdown">
        <Space
          className="eqc-dropdown-space width-percent-100"
          orientation="vertical"
        >
          {isSelectVisible ? (
            <div className="eqc-select-wrapper">
              <Select
                ref={selectRef}
                mode="multiple"
                placeholder="Select EQC"
                allowClear
                value={selectedValues}
                onChange={handleChange}
                onBlur={handleSelectBlur}
                tagRender={(props) => (
                  <EditableHyperlinkTag
                    {...props}
                    options={options}
                    hasAccess={hasAccess}
                  />
                )}
                options={options}
                disabled={disabled}
                open={isDropdownOpen}
                onOpenChange={setIsDropdownOpen}
                maxTagCount={MAX_TAG_DISPLAY_COUNT}
                showSearch={{ onSearch: handleSearch, filterOption: false }}
                onPopupScroll={handlePopupScroll}
                loading={loading}
                popupRender={(menu) => (
                  <>
                    {menu}
                    {loading && (
                      <div className="p-5 d-flex justify-center">
                        <Spin size="small" />
                      </div>
                    )}
                  </>
                )}
                notFoundContent={
                  !loading && options?.length === 0 ? 'No Data' : null
                }
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip
                    classNames={{ root: 'eqc-hyperlink-tooltip' }}
                    title={renderTooltipContent(
                      omittedValues,
                      options,
                      false,
                      hasAccess,
                    )}
                    placement="top"
                  >
                    <Tag color="#f2f2f2" className="hyperlink-tag">
                      <span className="eqc-tag-text">
                        +{omittedValues?.length} EQC
                      </span>
                    </Tag>
                  </Tooltip>
                )}
                className="width-percent-100"
              />
            </div>
          ) : (
            <div className="eqc-readonly-wrapper">
              {isEmpty ? <ReadOnlyPlaceholder /> : <ReadOnlyTextView />}
            </div>
          )}
        </Space>
      </div>
    );
  },
);

export default EqcDropdown;
